import { useEffect, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiFetch } from "./client";

interface ListResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  facets?: Record<string, string[]>;
}

/** How often a live table re-queries. Fast enough to read as "watching it
 *  happen" while a batch ingests; nothing downstream moves faster, since
 *  detection itself only sweeps every 15 minutes. */
export const LIVE_INTERVAL_MS = 5000;

/** Why polling is suspended even though the user left the toggle on. `null`
 *  means it is actually polling (or the toggle is off, which needs no
 *  explanation -- the toggle itself shows that). */
export type PauseReason = "page" | "hidden" | null;

function useTabVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  );
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
}

/** `initialFilters` seeds the column filters once, on mount -- how a link
 *  (e.g. a Rejections summary group) opens a table already narrowed. Later
 *  changes to it are ignored; the user owns the filters from then on. */
export function useTableQuery<T>(path: string, baseParams: Record<string, string>, limit = 50,
                                 initialFilters: Record<string, string> = {}) {
  const [offset, setOffset] = useState(0);
  const [sort, setSortState] = useState<string | undefined>(undefined);
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [filters, setFilters] = useState<Record<string, string>>(initialFilters);
  const [live, setLive] = useState(true);
  const visible = useTabVisible();

  // Sorting or filtering while on page 3 would request an offset the filtered
  // result no longer has, rendering an empty table that reads as a bug.
  const setSort = (sortKey: string, nextDir: "asc" | "desc") => {
    setSortState(sortKey);
    setDir(nextDir);
    setOffset(0);
  };
  const setFilter = (param: string, value: string) => {
    setFilters((prev) => ({ ...prev, [param]: value }));
    setOffset(0);
  };
  const clearFilters = () => {
    setFilters({});
    setOffset(0);
  };

  // Two situations where a moving table is worse than a stale one, so polling
  // suspends itself without switching the toggle off:
  //
  //   page   -- past page 1 the user is reading specific rows, and newly
  //             ingested transactions would reshuffle them mid-read.
  //   hidden -- a portal tab left open all day would otherwise fire thousands
  //             of requests at the API and RDS for nobody's benefit, and
  //             browsers throttle background timers unpredictably, so the
  //             "updated Xs ago" label would quietly start lying.
  //
  // Checked in that order: paging is the reason the user can act on.
  const pauseReason: PauseReason = !live ? null : offset > 0 ? "page" : !visible ? "hidden" : null;
  const shouldPoll = live && pauseReason === null;

  const active = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
  const query = new URLSearchParams({
    ...baseParams,
    ...active,
    ...(sort ? { sort, dir } : {}),
    limit: String(limit),
    offset: String(offset),
  }).toString();

  const { data, isLoading, isFetching, error, dataUpdatedAt } = useQuery({
    queryKey: [path, baseParams, active, sort, dir, offset, limit],
    queryFn: () => apiFetch<ListResponse<T>>(`${path}?${query}`),
    // Keep the previous page's rows/total on screen while a refetch is in
    // flight -- otherwise `total` transiently drops to 0 between keystrokes
    // in a text filter, and DataTable's empty state would unmount the very
    // input the user is typing into (see DataTable's total===0 handling).
    placeholderData: keepPreviousData,
    // A failing query stops polling. apiFetch already logs the user out on an
    // unrecoverable 401, but a persistent 500 has no such exit -- without this
    // it would be retried every 5 seconds for as long as the tab is open.
    refetchInterval: (q) =>
      shouldPoll && q.state.status !== "error" ? LIVE_INTERVAL_MS : false,
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    facets: data?.facets ?? {},
    limit,
    offset,
    setOffset,
    sort,
    dir,
    setSort,
    filters,
    setFilter,
    clearFilters,
    isLoading,
    isFetching,
    live,
    setLive,
    pauseReason,
    dataUpdatedAt,
    // Pre-assembled for DataTable's `live` prop -- structurally a LiveBinding,
    // built here rather than in each page so the three call sites stay one
    // line and cannot drift apart.
    liveBinding: { live, onLiveChange: setLive, pauseReason, dataUpdatedAt, isFetching },
    error: error as Error | undefined,
  };
}
